import {
    Box,
    Dropdown,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemDecorator,
    ListSubheader,
    Menu,
    MenuButton,
    MenuItem,
    Modal,
    ModalDialog,
    Sheet,
    Stack,
    useTheme
} from "@mui/joy";
import {statusPalettes, useSelfWorkStatusPalette} from "../agent/diary.tsx";
import {Link as RouterLink, useNavigate, useRouter} from "@tanstack/react-router";
import {isSameLink, useRouterSnaplink} from "../routing/useRouterSnaplink.tsx";
import {agentRoleIcons, useSelf} from "../agent/useSelf.ts";
import {useNaviState} from "../navi/Navi.tsx";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {LF_DEBUG_KEY, useAuthenticator, useAutologin} from "../auth/authenticator.tsx";
import {useBomb} from "../bomb/bomb.tsx";
import {useTranslation} from "react-i18next";
import {useQuery} from "@tanstack/react-query";
import {isDebugModeTqo} from "../routes/__root.tsx";
import {useState} from "react";
import {faArrowAltLeft, faCheck, faGear} from "@fortawesome/pro-regular-svg-icons";
import {keyify} from "../util/arutil.tsx";
import {faCaretDown} from "@fortawesome/pro-solid-svg-icons";
import {SetlistConnectionStatusIcon} from "../rpc/setlist.tsx";
import {RefreshButton} from "./RefreshButton.tsx";
import localforage from "localforage";
import {ShiftBreakDialogGu} from "../agent/ShiftBreakDialog.tsx";
import {useMothership} from "../tq/tqx.tsx";

export function TopBar() {
    const theme = useTheme();
    const {data: statusPaletteData} = useSelfWorkStatusPalette();
    const statusPalette = statusPaletteData ?? statusPalettes.out;
    const appBarFontSize = theme.fontSize.xl2;
    const router = useRouter();
    const routerSnaplink = useRouterSnaplink();
    const {data: self} = useSelf();
    const roleIcons = self ? agentRoleIcons(self) : [];
    const {blocks: naviBlocks, active: activeNaviItemProps}
        = useNaviState();
    const triggerLabel = activeNaviItemProps?.children;
    const triggerIcon = activeNaviItemProps?.icon;
    const triggerIconDecorator
        = triggerIcon ? {startDecorator: <FontAwesomeIcon {...triggerIcon}/>} : {};
    const autologin = useAutologin();
    const wantsAutologin = autologin.wantsAutologin;
    const bomb = useBomb();
    const {t} = useTranslation();
    const navigate = useNavigate();
    const auther = useAuthenticator();
    const mama = useMothership();
    const outletSlug = auther.getOutletSlug();
    const {data: isDebugMode} = useQuery(isDebugModeTqo);
    const gearMenuItemSx = {display: "flex", justifyContent: "flex-end"};
    const [isShiftBreakDialogOpen, setIsShiftBreakDialogOpen]
        = useState<boolean>(false);

    return <>
        <Sheet id="topbar"
               variant="solid" color={statusPalette} invertedColors
               sx={{width: "100%"}}>
            <Stack direction="row"
                   sx={{py: 1, justifyContent: "space-between", alignItems: "center"}}>
                <Stack direction="row" spacing={2.4}>
                    <IconButton variant="outlined" sx={{mr: 1}} onClick={() => {
                        router.history.back();
                    }}><FontAwesomeIcon icon={faArrowAltLeft} size="2x"/></IconButton>
                    <Stack direction="row" sx={{
                        alignItems: "center",
                        p: 0.4,
                        cursor: "pointer",
                    }} onClick={() => {
                        setIsShiftBreakDialogOpen(true);
                    }}>
                        {keyify(roleIcons).map((ki) => {
                            return <Box
                                key={ki.key} sx={{pr: 1}}>
                                <FontAwesomeIcon {...ki.elem} size="2x"/>
                            </Box>;
                        })}
                        <Box sx={{fontSize: appBarFontSize}}>
                            {self?.displayName}
                        </Box>
                    </Stack>
                </Stack>
                <Sheet invertedColors={true} color={statusPalette}>
                    <Dropdown>
                        <MenuButton variant="outlined" sx={{
                            bgcolor: theme.palette[statusPalette][700],
                            color: theme.palette[statusPalette][50],
                            "&:hover": {
                                backgroundColor: theme.palette[statusPalette][50],
                                color: theme.palette[statusPalette][700],
                            },
                        }}
                                    size="lg"
                                    endDecorator={<FontAwesomeIcon icon={faCaretDown}/>}
                                    {...triggerIconDecorator}>
                            {triggerLabel}
                        </MenuButton>
                        <Menu>
                            {naviBlocks.map(block => {
                                // TODO: figure out how to avoid the any cast
                                return <ListItem
                                    key={`navimenu-${block.subheader}-block`}
                                    nested>
                                    <ListSubheader>{block.subheader}</ListSubheader>
                                    <List>
                                        {keyify(block.itemProps).map(({
                                                                          key,
                                                                          elem: itemProps
                                                                      }) => {
                                            // this seems to be conflicting types battling between joy and the router
                                            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                                            const anyParams = itemProps.params as any;
                                            return <ListItem component={MenuItem}
                                                             key={key}>
                                                <ListItemDecorator>
                                                    {!!routerSnaplink && isSameLink(itemProps, routerSnaplink)
                                                        &&
                                                        <FontAwesomeIcon
                                                            icon={faCheck}/>}
                                                </ListItemDecorator>
                                                <ListItemButton component={RouterLink}
                                                                to={itemProps.to}
                                                                params={anyParams}
                                                                disabled={itemProps.disabled}>
                                                    {itemProps.icon &&
                                                        <FontAwesomeIcon {...itemProps.icon}/>
                                                    }
                                                    {itemProps.children
                                                        ??
                                                        <Box>{itemProps.to ?? itemProps.path}</Box>}
                                                </ListItemButton>
                                            </ListItem>
                                        })}
                                    </List>
                                </ListItem>
                            })}
                        </Menu>
                    </Dropdown>
                </Sheet>
                <Stack direction="row" spacing={0.8} sx={{alignItems: "center"}}>
                    <SetlistConnectionStatusIcon/>
                    <RefreshButton variant="soft" color="primary" sx={{p: 1}}/>
                </Stack>
                <Box>
                    <Dropdown>
                        <MenuButton slots={{root: IconButton}}
                                    slotProps={{
                                        root: {
                                            color: statusPalette,
                                            variant: "outlined"
                                        }
                                    }}>
                            <FontAwesomeIcon size="2x" icon={faGear}/>
                        </MenuButton>
                        <Menu>
                            <MenuItem sx={gearMenuItemSx}>{`outlet ${outletSlug}`}</MenuItem>
                            <MenuItem sx={gearMenuItemSx} onClick={() => {
                                navigate({to: "/self/prefs"}).catch(bomb);
                            }}>{t("userPrefs.title")}</MenuItem>
                            {import.meta.env.DEV
                                ?
                                <MenuItem
                                    sx={gearMenuItemSx}
                                    onClick={() => {
                                        autologin.setWantsAutologin(!wantsAutologin)
                                            .catch(bomb);
                                    }}>
                                    {wantsAutologin
                                        ?
                                        <ListItemDecorator><FontAwesomeIcon
                                            icon={faCheck}/></ListItemDecorator>
                                        : null}
                                    {t("autologin")}
                                </MenuItem>
                                : null}
                            <MenuItem sx={gearMenuItemSx}
                                      onClick={() => {
                                          void localforage.setItem(LF_DEBUG_KEY, !isDebugMode);
                                      }}>
                                {isDebugMode ?
                                    <ListItemDecorator><FontAwesomeIcon
                                        icon={faCheck}/></ListItemDecorator>
                                    : null}
                                debug
                            </MenuItem>
                            <MenuItem sx={{display: "flex", justifyContent: "flex-end"}}
                                      onClick={() => {
                                          void mama.logout()
                                              .then(() => auther.performLogout())
                                              .finally(() => void navigate({to: "/login", replace: true}))
                                      }}>{t("logout")}</MenuItem>
                        </Menu>
                    </Dropdown>
                </Box>
            </Stack>
        </Sheet>
        <Modal open={isShiftBreakDialogOpen}
               onClose={() => {
                   setIsShiftBreakDialogOpen(false);
               }} sx={{display: "flex", justifyContent: "center", alignItems: "center"}}>
            <ModalDialog>
                <ShiftBreakDialogGu onClose={() => {
                    setIsShiftBreakDialogOpen(false);
                }}/>
            </ModalDialog>
        </Modal>
    </>;
}

